'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, {
    createContext,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';
import { useIntersection, useMeasure } from 'react-use';

import cx from 'classnames';
import PropTypes from 'prop-types';

import Button from 'components/ui/Button';
import SvgIcon from 'components/ui/SvgIcon';

import Slide from './Slide';

import styles from './Carousel.module.scss';

export const CarouselContext = createContext({
    activeIndices: [],
    setActiveIndices: () => {},
    hasOverflow: true,
    isBeginning: true,
    isEnd: false,
    scrollToIndex: () => {},
    onIsBeginning: () => {},
});

const Carousel = forwardRef(
    (
        {
            children,
            className,
            hasNavigation = true,
            hideScrollBars = false,
            navigationTheme = 'blue',
            slideClassName,
            containerClassName,
            autoScroll = true,
            inlineIntro,
            disableNavigation = false,
            onIsBeginning,
        },
        ref
    ) => {
        const [scrollLeft, setScrollLeft] = useState(0);
        const [activeIndices, setActiveIndices] = useState([]);
        const [isInView, setIsInView] = useState(false);
        const [isInViewForKeys, setIsInViewForKeys] = useState(false);

        const rootRef = useRef(null);
        const containerRef = useRef(null);
        const trackRef = useRef(null);

        const [widthRef, { width: containerWidth }] = useMeasure();
        const trackWidth = trackRef?.current?.scrollWidth;
        const slideWith =
            trackWidth && children.length > 0
                ? trackWidth / children.length
                : 0;

        const isBeginning = Math.floor(scrollLeft) === 0;
        const isEnd = Math.ceil(containerWidth + scrollLeft) >= trackWidth;

        // Reset function to scroll to position 0 and reset index
        const reset = useCallback(() => {
            if (containerRef?.current) {
                containerRef.current.scrollTo({ left: 0 });
                setScrollLeft(0);
            }
        }, []);

        // Expose reset function to parent components
        useImperativeHandle(
            ref,
            () => ({
                reset,
            }),
            [reset]
        );

        const intersection = useIntersection(rootRef, {
            root: null,
            rootMargin: '0px',
            threshold: 0,
        });

        const intersectionForKeys = useIntersection(rootRef, {
            root: null,
            rootMargin: '0px',
            threshold: 0,
        });

        const handleNavigation = useCallback(
            direction => {
                const scroll = {
                    prev: slideWith * -1,
                    next: slideWith,
                };

                containerRef.current.scrollBy({
                    left: scroll[direction],
                });
            },
            [slideWith]
        );

        const handleContainerScroll = () => {
            setScrollLeft(containerRef?.current?.scrollLeft);
        };

        const scrollToIndex = index => {
            if (autoScroll) {
                const scrollLeft = index * slideWith;

                containerRef.current.scrollTo({
                    left: scrollLeft,
                    behavior: 'smooth',
                });
            }
        };

        useEffect(() => {
            onIsBeginning && onIsBeginning(isBeginning);
        }, [isBeginning, onIsBeginning]);

        useEffect(() => {
            containerRef?.current && containerRef.current.scrollTo({ left: 0 });
        }, []);

        useEffect(() => {
            if (intersection?.isIntersecting) {
                setIsInView(true);
            }
        }, [intersection]);

        useEffect(() => {
            setIsInViewForKeys(!!intersectionForKeys?.isIntersecting);
        }, [intersectionForKeys]);

        useEffect(() => {
            const handleKeyUp = event => {
                switch (event.key) {
                    case 'ArrowLeft':
                        handleNavigation('prev');
                        break;
                    case 'ArrowRight':
                        handleNavigation('next');
                        break;
                    default:
                        break;
                }
            };

            if (isInViewForKeys && !disableNavigation) {
                window.addEventListener('keyup', handleKeyUp);
            } else {
                window.removeEventListener('keyup', handleKeyUp);
            }

            return () => {
                window.removeEventListener('keyup', handleKeyUp);
            };
        }, [isInViewForKeys, handleNavigation, disableNavigation]);

        useEffect(() => {
            const container = containerRef.current;

            container.addEventListener('scroll', handleContainerScroll);

            return () =>
                container.removeEventListener('scroll', handleContainerScroll);
        }, [containerRef]);

        return (
            <CarouselContext.Provider
                value={{
                    activeIndices,
                    isBeginning,
                    isEnd,
                    setActiveIndices,
                    scrollToIndex,
                }}
            >
                <section
                    ref={rootRef}
                    className={cx(styles.root, className, {
                        [styles.isInView]: isInView,
                        [styles.hideScrollBars]: hideScrollBars,
                        [styles.isBeginning]: isBeginning,
                        [styles.inlineIntro]: inlineIntro,
                        [styles.disableNavigation]: disableNavigation,
                    })}
                >
                    <div
                        ref={containerRef}
                        className={cx(styles.container, containerClassName)}
                        inert={disableNavigation || undefined}
                    >
                        <div ref={widthRef}>
                            <ul ref={trackRef} className={styles.track}>
                                {React.Children.map(
                                    children,
                                    (child, index) => {
                                        return (
                                            <Slide
                                                root={containerRef}
                                                key={child.key}
                                                index={index}
                                                className={cx(
                                                    styles.slide,
                                                    slideClassName
                                                )}
                                            >
                                                {React.cloneElement(child)}
                                            </Slide>
                                        );
                                    }
                                )}
                            </ul>
                        </div>
                    </div>
                    {hasNavigation && (
                        <div
                            className={cx(
                                styles.navigation,
                                styles[navigationTheme]
                            )}
                        >
                            <Button
                                theme="none"
                                className={cx(styles.button, styles.prev)}
                                onClick={() => handleNavigation('prev')}
                                disabled={isBeginning || disableNavigation}
                                gtm-label="previous"
                                aria-label="previous"
                            >
                                <SvgIcon
                                    className={styles.icon}
                                    type="chevronLeft"
                                />
                            </Button>
                            <Button
                                theme="none"
                                className={cx(styles.button, styles.next)}
                                onClick={() => handleNavigation('next')}
                                disabled={isEnd || disableNavigation}
                                gtm-label="next"
                                aria-label="next"
                            >
                                <SvgIcon
                                    className={styles.icon}
                                    type="chevronRight"
                                />
                            </Button>
                        </div>
                    )}
                </section>
            </CarouselContext.Provider>
        );
    }
);

Carousel.displayName = 'Carousel';

Carousel.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
    className: PropTypes.string,
    navigationTheme: PropTypes.oneOf(['blue', 'cream', 'orange']),
    slideClassName: PropTypes.string,
    containerClassName: PropTypes.string,
    hasNavigation: PropTypes.bool,
    hideScrollBars: PropTypes.bool,
    autoScroll: PropTypes.bool,
    inlineIntro: PropTypes.bool,
    disableNavigation: PropTypes.bool,
    onIsBeginning: PropTypes.func,
};

export default Carousel;
